import React, { useState, useEffect } from "react";
import Login from "./Component/Login.jsx";
import Dashboard from "./Component/Dashboard.jsx";
import api from "./api";
import "./styles/App.css";
import TopBar from "./Component/Screen/TopBar";
import Sidebar from "./Component/Screen/Sidebar";
import PageSplash from "./Component/PageSplash";
import ProfilePage from "./Component/Screen/ProfilePage";
import SalesByDateTab from "./Component/Tabs/SalesByDateTab.jsx";
import TopProductsTab from "./Component/Tabs/TopProductsTab.jsx";
import CustomersTab from "./Component/Tabs/CustomersTab.jsx";
import SalesByCategoryTab from "./Component/Tabs/SalesByCategoryTab.jsx";
import TopCategoriesTab from "./Component/Tabs/TopCategoriesTab.jsx";
import SuppliersTab from "./Component/Tabs/SuppliersTab.jsx";
import BalancesTab from "./Component/Tabs/BalancesTab.jsx";
import PurchaseByDateTab from "./Component/Tabs/PurchaseByDateTab.jsx";
import ExpenseByCategoryTab from "./Component/Tabs/ExpenseByCategoryTab.jsx";
import ProductsTab from "./Component/Tabs/ProductsTab";
import StockValueTab from "./Component/Tabs/StockValueTab";
import SwitchCompanyModal from "./Component/Shared/SwitchCompanyModal.jsx";
import ToastProvider, { useToast } from "./Component/Shared/ToastProvider.jsx";
import SubscriptionProvider, {
  useSubscription,
} from "./Component/Shared/SubscriptionProvider.jsx";
import DisabledOverlay from "./Component/Shared/DisabledOverlay.jsx";
import {
  setGlobalToastHandler,
  setGlobalSubscriptionHandler,
} from "./utils/globalNotifications";

const STORAGE_PREFIX = "wv_";
const TOKEN_KEY = STORAGE_PREFIX + "token";
const USER_KEY = STORAGE_PREFIX + "user";
const API_KEYS_KEY = STORAGE_PREFIX + "api_keys";
const COMPANIES_KEY = STORAGE_PREFIX + "companies";
const API_KEY_KEY = STORAGE_PREFIX + "api_key";
const SELECTED_COMPANY_KEY = STORAGE_PREFIX + "selected_company_id";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 900);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

function readStored(key) {
  try {
    const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.hasOwnProperty("value") &&
      parsed.hasOwnProperty("expiresAt")
    ) {
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeStored(key, value, { persistent = true, ttlMs = null } = {}) {
  const target = persistent ? localStorage : sessionStorage;
  try {
    // const payload = ttlMs
    //   ? JSON.stringify({ value, expiresAt: Date.now() + ttlMs })
    //   : JSON.stringify(value);

    const payload = JSON.stringify(value);
    target.setItem(key, payload);
  } catch (e) {}
}

function removeStored(...keys) {
  keys.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

const SESSION_TTL_MS = 12 * 60 * 60 * 1000;
const REMEMBER_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const getAuthHeaders = (apiKey = null) => {
  const token = readStored(TOKEN_KEY);
  const effectiveApiKey = apiKey || readStored(API_KEY_KEY);
  const headers = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (effectiveApiKey) {
    headers["Authorization"] = `Bearer ${effectiveApiKey}`;
  }
  if (effectiveApiKey) headers["X-API-KEY"] = effectiveApiKey;
  return headers;
};

function AppInner() {
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();
  const {
    isExpired,
    markAsExpired,
    reset: resetSubscription,
    checkSubscriptionStatus,
  } = useSubscription();

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 600);
  }, []);

  useEffect(() => {
    setGlobalToastHandler(showError);
    setGlobalSubscriptionHandler(markAsExpired);
  }, [showError, markAsExpired]);

  const [user, setUser] = useState(() => readStored(USER_KEY));

  const [companies, setCompanies] = useState(
    () => readStored(COMPANIES_KEY) || []
  );

  const [currentApiKey, setCurrentApiKey] = useState(
    readStored(API_KEY_KEY) || ""
  );
  const [company, setCompany] = useState(null);
  const [kpiTiles, setKpiTiles] = useState([]);
  const [rememberMode, setRememberMode] = useState(
    () => !!localStorage.getItem(TOKEN_KEY)
  );
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("dashboard");
  const [showPageSplash, setShowPageSplash] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSwitchCompanyModal, setShowSwitchCompanyModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const showSplashThen = (action, ms = 420) => {
    setShowPageSplash(true);
    setTimeout(() => {
      try {
        action && action();
      } finally {
        setShowPageSplash(false);
      }
    }, ms);
  };

  const isMobile = useIsMobile();
  const toggleSidebar = () => {
    if (isMobile) setSidebarOpen((s) => !s);
    else setSidebarCollapsed((s) => !s);
  };
  const closeSidebar = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const handleProfileClick = () => {
    // Profile page is disabled for now
    // showSplashThen(() => {
    //   setSidebarTab("profile");
    //   setShowProfile(false);
    // });
  };
  const handleCloseProfile = () => {
    setShowProfile(false);
    if (sidebarTab === "profile") setSidebarTab("dashboard");
  };
  const handleLogout = () => {
    showSplashThen(() => {
      removeStored(
        TOKEN_KEY,
        API_KEY_KEY,
        USER_KEY,
        API_KEYS_KEY,
        COMPANIES_KEY
      );
      setUser(null);
      setCompany(null);
      setCompanies([]);
      setCurrentApiKey("");
      resetSubscription();

      try {
        localStorage.removeItem(SELECTED_COMPANY_KEY);
      } catch (e) {}
      setShowProfile(false);
    });
  };

  const onCompanyChange = (selectedKey) => {
    if (!selectedKey) return;
    try {
      // writeStored(API_KEY_KEY, selectedKey, { persistent: rememberMode });
      writeStored(API_KEY_KEY, selectedKey);
    } catch (e) {}
    setCurrentApiKey(selectedKey);
    try {
      resetSubscription();
    } catch (e) {}
    try {
      const found =
        companies.find((c) => c.api_key === selectedKey) ||
        companies.find((c) => String(c.id) === String(selectedKey));
      const companyId = found ? found.id || null : null;
      if (companyId)
        localStorage.setItem(SELECTED_COMPANY_KEY, String(companyId));
      else localStorage.removeItem(SELECTED_COMPANY_KEY);
    } catch (e) {}
    setCompany(null);
    setKpiTiles([]);
    setSelectedKpi(null);
    setSelectedDate("");
    setSidebarTab("dashboard");
    setShowProfile(false);
  };

  useEffect(() => {
    if (user) {
      // writeStored(USER_KEY, user, {
      //   persistent: rememberMode,
      //   ttlMs: rememberMode ? REMEMBER_TTL_MS : SESSION_TTL_MS,
      // });
      writeStored(USER_KEY, user);
    } else {
      removeStored(USER_KEY);
    }
  }, [user, rememberMode]);

  useEffect(() => {
    if (companies.length > 0) {
      // writeStored(COMPANIES_KEY, companies, {
      //   persistent: rememberMode,
      //   ttlMs: rememberMode ? REMEMBER_TTL_MS : SESSION_TTL_MS,
      // });
      writeStored(COMPANIES_KEY, companies);
    } else {
      removeStored(COMPANIES_KEY);
    }
  }, [companies, rememberMode]);

  useEffect(() => {
    if (user && companies.length > 0) {
      const selected = currentApiKey
        ? companies
            .filter(
              (c) =>
                c.api_key === currentApiKey ||
                String(c.id) === String(currentApiKey)
            )
            .map((c) => ({ company: c }))
        : companies.map((c) => ({ company: c }));
      checkSubscriptionStatus(selected);
    }
  }, [user, companies, currentApiKey, checkSubscriptionStatus]);

  useEffect(() => {
    if (!user || !currentApiKey) {
      setCompany(null);
      return;
    }

    try {
      const found =
        companies.find((c) => c.api_key === currentApiKey) ||
        companies.find((c) => String(c.id) === String(currentApiKey));
      setCompany(found || null);
    } catch (e) {
      setCompany(null);
    }
  }, [user, currentApiKey, companies]);

  useEffect(() => {
    if (!user || !currentApiKey) return;
    api
      .get("/v1/frontend/tiles", { headers: getAuthHeaders(currentApiKey) })
      .then((res) => {
        const rawTiles =
          res.data?.data?.data ||
          res.data?.data?.tiles ||
          res.data?.tiles ||
          res.data?.data ||
          res.data ||
          [];

        const normalized = Array.isArray(rawTiles)
          ? rawTiles.map((t) => {
              const rawDate = t.kpi_date || t.date || t.kpiDate || null;
              const kpi_date = rawDate ? String(rawDate).split("T")[0] : null;

              let tilesObj = t.tiles;
              if (typeof tilesObj === "string") {
                try {
                  tilesObj = JSON.parse(tilesObj);
                } catch (e) {}
              }

              return { ...t, kpi_date, tiles: tilesObj };
            })
          : [];

        setKpiTiles(normalized);

        if (normalized.length > 0) {
          const first = normalized[0];
          if (first?.kpi_date) setSelectedDate(first.kpi_date);
          setSelectedKpi(first);
        } else {
          setSelectedDate("");
          setSelectedKpi(null);
        }
      })
      .catch((err) => {
        setKpiTiles([]);
        setSelectedDate("");
        setSelectedKpi(null);
      });
  }, [user, currentApiKey]);

  useEffect(() => {
    if (selectedDate && kpiTiles.length > 0) {
      const found = kpiTiles.find((tile) => tile.kpi_date === selectedDate);
      setSelectedKpi(found || null);
    }
  }, [selectedDate, kpiTiles]);

  if (loading) {
    return (
      <div className="app-bg full-bg">
        <PageSplash />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="full-bg">
        {showPageSplash && <PageSplash />}
        <div className="login-wrapper full-login">
          <Login
            onLogin={(user, keys, options) => {
              showSplashThen(() => {
                let updatedUser = { ...user };
                if (!updatedUser.avatar) {
                  const avatarData = localStorage.getItem("avatar_dataurl");
                  if (avatarData) updatedUser.avatar = avatarData;
                }
                setUser(updatedUser);
                resetSubscription();

                try {
                  // writeStored(USER_KEY, updatedUser, {
                  //   persistent: options?.remember,
                  //   ttlMs: options?.remember ? REMEMBER_TTL_MS : SESSION_TTL_MS,
                  // });
                  writeStored(USER_KEY, updatedUser);
                  if (updatedUser.avatar) {
                    localStorage.setItem("avatar_dataurl", updatedUser.avatar);
                  }
                } catch (e) {}
                setCompanies(keys || []);
                if (keys && keys.length > 0) {
                  const defaultKey =
                    keys[0].api_key ||
                    keys[0].apiKey ||
                    keys[0].key ||
                    keys[0].id;
                  setCurrentApiKey(defaultKey);
                  // writeStored(API_KEY_KEY, defaultKey, {
                  //   persistent: options?.remember,
                  //   ttlMs: options?.remember ? REMEMBER_TTL_MS : SESSION_TTL_MS,
                  // });
                  writeStored(API_KEY_KEY, defaultKey);
                  try {
                    const found = keys[0];
                    const companyId =
                      found && found.company ? found.company.id || null : null;
                    if (companyId)
                      localStorage.setItem(
                        SELECTED_COMPANY_KEY,
                        String(companyId)
                      );
                  } catch (e) {}
                }
                if (options?.token) {
                  writeStored(TOKEN_KEY, options.token);
                  // writeStored(TOKEN_KEY, options.token, {
                  //   persistent: options.remember,
                  //   ttlMs: options.remember ? REMEMBER_TTL_MS : SESSION_TTL_MS,
                  // });
                }
                if (typeof options?.remember === "boolean") {
                  setRememberMode(options.remember);
                }
              });
            }}
          />
        </div>
      </div>
    );
  }

  const authHeaders = getAuthHeaders(currentApiKey);

  return (
    <div className="app-bg full-bg">
      <div className="dashboard-layout full-layout">
        {isMobile ? (
          <>
            <Sidebar
              onSelect={(tab) => {
                setSidebarTab(tab);
                setSidebarOpen(false);
              }}
              selected={sidebarTab}
              hideKpi
              collapsed={false}
              onToggle={toggleSidebar}
              style={{
                transform: sidebarOpen ? "translateX(0)" : "translateX(-110%)",
                transition: "transform 0.3s cubic-bezier(.4,0,.2,1)",
                zIndex: 1200,
                position: "fixed",
                top: 0,
                left: 0,
                height: "100vh",
                boxShadow: sidebarOpen
                  ? "2px 0 16px rgba(0,0,0,0.12)"
                  : undefined,
              }}
            />
            {sidebarOpen && (
              <div
                className="sidebar-mobile-overlay"
                onClick={closeSidebar}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 1100,
                  transition: "background 0.2s cubic-bezier(.4,0,.2,1)",
                }}
                aria-label="Close sidebar overlay"
              />
            )}
          </>
        ) : (
          <Sidebar
            onSelect={(tab) => {
              setSidebarTab(tab);
            }}
            selected={sidebarTab}
            hideKpi
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
          />
        )}
        {showPageSplash && <PageSplash />}
        <div className="dashboard-main">
          <TopBar
            user={user}
            company={company}
            onProfileClick={handleProfileClick}
            onToggleSidebar={toggleSidebar}
            onLogout={handleLogout}
            apiKeys={companies}
            currentApiKey={currentApiKey}
            onCompanyChange={onCompanyChange}
            showSplashThen={showSplashThen}
          />

          <SwitchCompanyModal
            isOpen={showSwitchCompanyModal}
            onClose={() => setShowSwitchCompanyModal(false)}
            apiKeys={companies}
            currentApiKey={currentApiKey}
            onCompanyChange={onCompanyChange}
            showSplashThen={showSplashThen}
          />

          {showProfile && (
            <ProfilePage
              user={user}
              setUser={setUser}
              company={company}
              onClose={handleCloseProfile}
              apiKeys={companies}
              currentApiKey={currentApiKey}
              onCompanyChange={onCompanyChange}
            />
          )}

          {sidebarTab === "dashboard" && (
            <Dashboard
              key={currentApiKey}
              kpiTiles={kpiTiles}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedKpi={selectedKpi}
              headers={authHeaders}
            />
          )}
          {sidebarTab === "profile" && (
            <ProfilePage
              user={user}
              setUser={setUser}
              company={company}
              onClose={() => setSidebarTab("dashboard")}
              apiKeys={companies}
              currentApiKey={currentApiKey}
              onCompanyChange={onCompanyChange}
            />
          )}
          {sidebarTab === "salesByDate" && (
            <SalesByDateTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "topProducts" && (
            <TopProductsTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "customers" && (
            <CustomersTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "products" && (
              <ProductsTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "stockValue" && (
              <StockValueTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "balances" && (
            <BalancesTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "salesByCategory" && (
            <SalesByCategoryTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "purchaseByDate" && (
            <PurchaseByDateTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "expenseByCategory" && (
            <ExpenseByCategoryTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "topCategories" && (
            <TopCategoriesTab key={currentApiKey} headers={authHeaders} />
          )}
          {sidebarTab === "suppliers" && (
            <SuppliersTab key={currentApiKey} headers={authHeaders} />
          )}
        </div>

        {isExpired && (
          <DisabledOverlay
            onLogout={handleLogout}
            onSwitchCompany={() => setShowSwitchCompanyModal(true)}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <SubscriptionProvider>
        <AppInner />
      </SubscriptionProvider>
    </ToastProvider>
  );
}

export default App;
